/**
 * Reddit Ads Pixel — événements de conversion standard.
 * Nécessite `window.rdt` (initialisé dans util/includeScripts.js).
 * https://business.reddithelp.com/articles/Knowledge/supported-conversion-events
 */

import { types as sdkTypes } from './sdkLoader';
import { convertMoneyToNumber } from './currency';

const { Money } = sdkTypes;

const rdtTrack = (eventName, data) => {
  try {
    if (typeof window === 'undefined' || typeof window.rdt !== 'function') {
      return;
    }
    if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      window.rdt('track', eventName, data);
    } else {
      window.rdt('track', eventName);
    }
  } catch (_) {
    // Silently fail analytics
  }
};

/**
 * @param {Object} p
 * @param {number} p.value - Montant TTC (unités majeures)
 * @param {string} p.currency - ISO 4217
 * @param {string} p.transactionId - ID commande (dédup côté annonceur)
 * @param {number} [p.itemCount]
 */
export const trackRedditPurchase = ({ value, currency, transactionId, itemCount }) => {
  if (value == null || !currency || !transactionId) {
    return;
  }
  const payload = {
    value: Number(value),
    currency: String(currency).toUpperCase(),
    transactionId: String(transactionId),
  };
  if (itemCount != null && !Number.isNaN(Number(itemCount))) {
    payload.itemCount = Number(itemCount);
  }
  rdtTrack('Purchase', payload);
};

/**
 * @param {Object} [p]
 * @param {number} [p.value]
 * @param {string} [p.currency]
 * @param {number} [p.itemCount]
 */
export const trackRedditAddToCart = (p = {}) => {
  const { value, currency, itemCount } = p;
  if (value != null && currency) {
    const payload = {
      value: Number(value),
      currency: String(currency).toUpperCase(),
    };
    if (itemCount != null && !Number.isNaN(Number(itemCount))) {
      payload.itemCount = Number(itemCount);
    }
    rdtTrack('AddToCart', payload);
    return;
  }
  rdtTrack('AddToCart');
};

export const trackRedditLead = () => {
  rdtTrack('Lead');
};

export const trackRedditSignUp = () => {
  rdtTrack('SignUp');
};

/**
 * @param {string} searchString - Terme recherché
 */
export const trackRedditSearch = searchString => {
  const q = typeof searchString === 'string' ? searchString.trim() : '';
  if (!q) {
    return;
  }
  rdtTrack('Search', { search_string: q });
};

/**
 * Fiche produit (contenu consulté).
 * @param {Object} p
 * @param {string} p.id - Identifiant listing
 * @param {number} [p.value]
 * @param {string} [p.currency]
 */
export const trackRedditViewContent = p => {
  if (!p || !p.id) {
    return;
  }
  const payload = { item_id: String(p.id) };
  if (p.value != null && p.currency) {
    payload.value = Number(p.value);
    payload.currency = String(p.currency).toUpperCase();
  }
  rdtTrack('ViewContent', payload);
};

/**
 * @param {Object} listing - Entité listing Sharetribe
 */
export const trackRedditViewContentForListing = listing => {
  try {
    const id = listing?.id?.uuid || listing?.id;
    if (!id) {
      return;
    }
    const price = listing?.attributes?.price;
    if (price instanceof Money) {
      trackRedditViewContent({
        id,
        value: convertMoneyToNumber(price),
        currency: price.currency,
      });
    } else {
      trackRedditViewContent({ id });
    }
  } catch (_) {
    // Silently fail
  }
};
